import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient, getTokens, setTokens, clearTokens } from '../services/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // { id, email, ... } — from the API's `user` object
  const [profile, setProfile] = useState(null); // same object in this backend; kept separate for compatibility
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const me = await apiClient.get('/users/me');
      setUser(me);
      setProfile(me);
    } catch (e) {
      setUser(null);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    const { accessToken } = getTokens();
    if (accessToken) {
      loadMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadMe]);

  // Automatic activity tracking heartbeat checkpoints the open
  // session every 5 minutes (matches the backend's CHECKPOINT_INTERVAL_SECONDS)
  // so at most one interval's worth of time is ever at risk.
  useEffect(() => {
    if (!user) return;
    const HEARTBEAT_MS = 5 * 60 * 1000;
    const interval = setInterval(() => {
      apiClient.post('/auth/session/heartbeat', {}).catch(() => {}); // best-effort
    }, HEARTBEAT_MS);

    // Checkpoint when the tab is hidden (switched away from)
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        apiClient.post('/auth/session/heartbeat', {}).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  const signInWithEmail = async (email, password) => {
    const data = await apiClient.post('/auth/login', { email, password }, { auth: false });
    setTokens(data);
    await loadMe();
    return data;
  };

  const signUpWithEmail = async (email, password, extra = {}) => {
    const data = await apiClient.post('/auth/register', { email, password, ...extra }, { auth: false });
    setTokens(data);
    await loadMe();
    return data;
  };

  // OTP-based auth — same token/profile plumbing as the password flows above,
  // just gated by a verified email code instead of (or before) a password.
  const signUpWithOtp = async (email, password, otp, extra = {}) => {
    const data = await apiClient.post('/auth/otp/verify-register', { email, password, otp, ...extra }, { auth: false });
    setTokens(data);
    await loadMe();
    return data;
  };

  const signInWithOtp = async (email, otp) => {
    const data = await apiClient.post('/auth/otp/verify-login', { email, otp }, { auth: false });
    setTokens(data);
    await loadMe();
    return data;
  };

  // Google OAuth — connects to backend /auth/google endpoint
  const signInWithGoogle = async (customPayload) => {
    if (customPayload) {
      const data = await apiClient.post('/auth/google', customPayload, { auth: false });
      setTokens(data);
      await loadMe();
      return data;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    // Preferred: Google OAuth2 Popup Selector via GIS oauth2 token client
    if (window.google?.accounts?.oauth2 && clientId) {
      return new Promise((resolve, reject) => {
        try {
          const client = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: 'openid email profile',
            callback: async (tokenResponse) => {
              if (tokenResponse.error) {
                reject(new Error(tokenResponse.error_description || tokenResponse.error));
                return;
              }
              try {
                const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                  headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
                });
                const gProfile = await res.json();
                const data = await apiClient.post('/auth/google', {
                  googleId: gProfile.sub,
                  email: gProfile.email,
                  full_name: gProfile.name,
                  avatarUrl: gProfile.picture,
                }, { auth: false });
                setTokens(data);
                await loadMe();
                resolve(data);
              } catch (err) {
                reject(err);
              }
            },
          });
          client.requestAccessToken({ prompt: 'select_account' });
        } catch (err) {
          reject(err);
        }
      });
    }

    // Alternative: Google Identity Services ID Token One-Tap / Prompt
    if (window.google?.accounts?.id && clientId) {
      return new Promise((resolve, reject) => {
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: async (response) => {
              try {
                const data = await apiClient.post('/auth/google', { credential: response.credential }, { auth: false });
                setTokens(data);
                await loadMe();
                resolve(data);
              } catch (err) {
                reject(err);
              }
            }
          });
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              // fallback
            }
          });
        } catch (err) {
          reject(err);
        }
      });
    }

    // Default fallback demo login
    const data = await apiClient.post('/auth/google', {
      email: 'scholar.google@ekalavya.edu',
      full_name: 'Google Scholar',
      googleId: 'google-sub-987654321',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    }, { auth: false });
    setTokens(data);
    await loadMe();
    return data;
  };

  const signOut = async () => {
    const { refreshToken } = getTokens();
    setUser(null);
    setProfile(null);
    clearTokens();
    try {
      await apiClient.post('/auth/logout', { refreshToken }, { auth: false });
    } catch (_) { /* best-effort */ }
  };

  const syncProfile = async () => {
    await loadMe();
  };

  const updateProfile = async (updates) => {
    const updated = await apiClient.patch('/users/me', updates);
    setUser(updated);
    setProfile(updated);
    return updated;
  };

  const getPublicProfile = async (id) => {
    try {
      return await apiClient.get(`/users/${id}/portfolio`, { auth: false });
    } catch (e) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signInWithGoogle, signInWithEmail, signUpWithEmail, signUpWithOtp, signInWithOtp, signOut,
      syncProfile, updateProfile, getPublicProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
